import { atom } from 'jotai';
import {
  AlignmentIssuesTypes,
  AlignmentStatuses,
} from '../constants/alignment';
import {
  videoSelectedForAlignmentCheckAtom,
  videoReferenceSelectedForAlignmentCheckAtom,
} from './videoAlignmentRequired';
import { editingOffsetsAtom } from './review';

const inferPlaybackAndMinutesOk = (alignmentIssue, alignmentStatus) => {
  if (
    alignmentIssue === AlignmentIssuesTypes.UNLICENSED_VIDEO &&
    alignmentStatus === AlignmentStatuses.REQUIRES_RECOLLECTION
  ) {
    return true;
  }

  if (
    alignmentIssue === AlignmentIssuesTypes.LICENSED_VIDEO &&
    alignmentStatus === AlignmentStatuses.REQUIRES_NEW_HUDL_VIDEO
  ) {
    return false;
  }

  return null;
};

export const currentStepAtom = atom(0);
currentStepAtom.debugLabel = 'currentStepAtom';

const playbackAndMinutesOkBaseAtom = atom(null);
playbackAndMinutesOkBaseAtom.debugLabel = 'playbackAndMinutesOkBaseAtom';

export const playbackAndMinutesOkAtom = atom(
  (get) => {
    const event = get(videoSelectedForAlignmentCheckAtom);
    if (!event) return null;

    const value = get(playbackAndMinutesOkBaseAtom);
    if (value !== null && value !== undefined) {
      return value;
    }

    const alignmentIssue = event?.videoInfo?.alignmentIssue;
    const alignmentStatus = event?.videoInfo?.alignmentStatus;
    return inferPlaybackAndMinutesOk(alignmentIssue, alignmentStatus);
  },
  (_get, set, value) => {
    set(playbackAndMinutesOkBaseAtom, value);
  },
);
playbackAndMinutesOkAtom.debugLabel = 'playbackAndMinutesOkAtom';

const alignmentNotesBaseAtom = atom(null);
alignmentNotesBaseAtom.debugLabel = 'alignmentNotesBaseAtom';

export const alignmentNotesAtom = atom(
  (get) => {
    const event = get(videoSelectedForAlignmentCheckAtom);
    if (!event) return null;

    const value = get(alignmentNotesBaseAtom);

    if (value !== null && value !== undefined) {
      return value;
    }

    return event?.videoInfo?.alignmentNotes ?? '';
  },
  (_get, set, value) => {
    set(alignmentNotesBaseAtom, value);
  },
);
alignmentNotesAtom.debugLabel = 'alignmentNotesAtom';

export const draftPeriodStartAtom = atom(null);
draftPeriodStartAtom.debugLabel = 'draftPeriodStartAtom';

export const draftPeriodEndAtom = atom(null);
draftPeriodEndAtom.debugLabel = 'draftPeriodEndAtom';

export const draftIsAlignedAtom = atom(null);
draftIsAlignedAtom.debugLabel = 'draftIsAlignedAtom';

export const resetMultiStepAtoms = atom(null, (_get, set) => {
  set(playbackAndMinutesOkAtom, null);
  set(alignmentNotesAtom, null);
  set(currentStepAtom, 0);
  set(videoReferenceSelectedForAlignmentCheckAtom, null);
  set(editingOffsetsAtom, false);
  set(draftPeriodStartAtom, null);
  set(draftPeriodEndAtom, null);
  set(draftIsAlignedAtom, null);
});
resetMultiStepAtoms.debugLabel = 'resetMultiStepAtoms';

export const isModalOpenAtom = atom(
  (get) => !!get(videoReferenceSelectedForAlignmentCheckAtom),
);
isModalOpenAtom.debugLabel = 'isModalOpenAtom';

export const canProceedAtom = atom((get) => {
  const current = get(currentStepAtom);
  const periodStart = get(draftPeriodStartAtom);
  const periodEnd = get(draftPeriodEndAtom);
  const editingOffsets = get(editingOffsetsAtom);
  const isAligned = get(draftIsAlignedAtom);
  const playbackAndMinutesOk = get(playbackAndMinutesOkAtom);
  const alignmentNotes = get(alignmentNotesAtom);

  switch (current) {
    case 0:
      return periodStart && periodEnd && !editingOffsets;
    case 1:
      return (
        isAligned || playbackAndMinutesOk !== null || alignmentNotes !== ''
      );
    default:
      return false;
  }
});
canProceedAtom.debugLabel = 'canProceedAtom';
